import { useState, useRef } from "react";
import classRoomImg from "../../../../assets/img/teacherDashboard/Classroom.png";
import { Form, Button, Card } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import "bootstrap/dist/css/bootstrap.min.css";
import "./CreateLesson.css";
import { useCreateLesson } from "../../../../hooks/teacher/lessons/useCreateLesson";
import { useAssignClassrooms } from "../../../../hooks/teacher/lessons/useAssignClassrooms";
import BackButton from "../../TeacherAdmin/Goback/GoBack";
import QuestSelector from "./QuestSelector";
import ClassroomSelector from "./ClassroomSelector";
import { useAssignQuestToLesson } from "../../../../hooks/teacher/lessons/useAssignQuestToLesson";
import CustomInput from "../../../common/CustomInput";
import CustomTextArea from "../../../common/CustomTextArea";
import SuccessModal from "../../../common/SuccessModal";
import ScrollToError from "../../../../utils/ScrollToError";

const formatDateTime = (date) => {
	return date.includes(" ") ? date : `${date} 00:00:00`;
};

const CreateLesson = () => {
	const [assignedClasses, setAssignedClasses] = useState([]);

	const { createLesson, loading: creatingLesson, error } = useCreateLesson();
	const { assignClassrooms, loading: assigningClassroomLoader } =
		useAssignClassrooms();
	const { assignQuestToLesson, loading: assigningQuestLoader } =
		useAssignQuestToLesson();
	/**
 * Success message state
 * @typedef {Object} Message
 * @property {string} message - Success message
 * @property {string} description - Success description
 * @property {"success"|"error"} type - Success type
 */
	const [successMessage, setSuccessMessage] = useState(/** @type {Message} */({
		message: "",
		description: "",
		type: "success",
	}));
	const [showModal, setShowModal] = useState(false)

	const topRef = useRef(null);
	const lessonNameRef = useRef(null);
	const lessonDescriptionRef = useRef(null);

	const handleDueDateChange = (index, newDate) => {
		const updated = [...assignedClasses];
		updated[index].dueDate = newDate;
		setAssignedClasses(updated);
	};

	const removeClass = (index) => {
		setAssignedClasses(assignedClasses.filter((_, i) => i !== index));
	};



	const validationSchema = Yup.object({
		lessonName: Yup.string()
			.min(3, "Lesson name must be at least 3 characters long")
			.required("Lesson name is required")
			.max(100, "Lesson name must be at most 100 characters long")
			.matches(
				/^[A-Za-z0-9 _-]+$/,
				"Lesson name can only contain letters, numbers, spaces, underscores, and hyphens"
			),
		lessonDescription: Yup.string()
			.min(10, "Lesson Description must be at least 10 characters long")
			.required("Lesson Description is required")
			.max(1000, "Lesson Description must be at most 1000 characters long"),
		selectedQuests: Yup.array().min(1, "Select at least one Quest"),
	});

	/*
	 * Handle Submit function to create lesson , assign quests and assign lesson to classroom
	 */
	const handleSubmit = async (values, { resetForm }) => {
		setSuccessMessage({
			message: "",
			description: "",
			type: "success",
		});
		const questsData = values.selectedQuests.map((quest, index) => {
			return {
				id: quest.id,
				order_index: index + 1,
				is_assign: true,
			};
		});
		// Step 1. Create Lesson
		const response = await createLesson({
			lessonData: {
				title: values.lessonName,
				description: values.lessonDescription,
			},
		});

		// Step 2. Check if Lesson is created successfuly or not. If not stop else assign the quests to lesson
		if (!response) {
			topRef.current.scrollIntoView({ behavior: "smooth" });
			return;
		}

		// Step 3. Assign the quests to lesson
		const assignQuestsResponse = await assignQuestToLesson({
			lessonId: response?.data?.id,
			questsData,
		});

		// step 4. Assign the classrooms to lesson
		if (assignedClasses.length > 0) {
			const assignClassroomsResponse = await assignClassrooms({
				lessonId: response?.data?.id,
				assignmentData: assignedClasses.map((cls) => {
					return {
						id: cls.id,
						is_assign: true,
						due_date: formatDateTime(cls.dueDate || ""),
					};
				}),
			});

			if (!assignClassroomsResponse && !assignQuestsResponse) {
				setSuccessMessage(
					{
						message: "Lesson created successfully",
						description: "Lesson created successfully but unable to assign the classrooms to the lesson and quests to the lesson",
						type: "error",
					}
				);
				setShowModal(true)
				topRef.current.scrollIntoView({ behavior: "smooth" });
				return;
			}
			if (!assignClassroomsResponse) {
				setSuccessMessage(
					{
						message: "Lesson created successfully",
						description: "Lesson created successfully but unable to assign the classrooms to the lesson",
						type: "error",
					}
				);
				setShowModal(true)
				topRef.current.scrollIntoView({ behavior: "smooth" });
				return;
			}
		}

		if (!assignQuestsResponse) {
			setSuccessMessage(
				{
					message: "Lesson created successfully",
					description: "Lesson created successfully but unable to assign the quests to the lesson",
					type: "error",
				}
			);
			setShowModal(true)
			topRef.current.scrollIntoView({ behavior: "smooth" });
			return;
		}

		setSuccessMessage({
			message: "Lesson created successfully",
			description: "Lesson created successfully and Quests assigned to the lesson",
			type: "success",
		});
		setShowModal(true)
		topRef.current.scrollIntoView({ behavior: "smooth" });
		setAssignedClasses([]);

		resetForm();
		// setTimeout(() => {
		//   console.log("SetTimeout working");
		//   navigate("/teacher/lesson");
		// }, 2000);
	};
	return (
		<div className="">
			<div className="">
				<BackButton />
			</div>
			<div className="classroom-container" ref={topRef}>
				<img src={classRoomImg} className="img" alt="Classroom" />
				<h2 className="text">Create Lesson</h2>
			</div>

			<Card className="mt-4 p-4 shadow-sm border-0">
				<div>

					{/* Error Message */}
					{error && (
						<div className="alert alert-danger mt-3" role="alert">
							<i className="bi bi-exclamation-circle-fill me-2"></i>
							{error}
						</div>
					)}
				</div>
				<Formik
					initialValues={{
						lessonName: "",
						lessonDescription: "",
						selectedQuests: [],
					}}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({ handleSubmit, isSubmitting }) => {
						return (
							<Form onSubmit={handleSubmit}>
								<ScrollToError
									fieldRefs={{
										lessonName: lessonNameRef,
										lessonDescription: lessonDescriptionRef
									}}
								/>
								{/* LESSON NAME */}
								<div className="mb-3">
									<CustomInput
										type="text"
										name="lessonName"
										label="Lesson Name"
										placeholder="Lesson Name"
										autoComplete="off"
										layout="stacked"
										ref={lessonNameRef}
									/>
								</div>

								{/* LESSON DESCRIPTION */}
								<div className="mb-3">
									<CustomTextArea
										type="text"
										name="lessonDescription"
										label="Lesson Description"
										placeholder="Lesson Description"
										autoComplete="off"
										layout="stacked"
										ref={lessonDescriptionRef}
									/>
								</div>

								{/* QUESTS SELECTION (Dynamic Tables) */}
								<div className="mt-4">
									<QuestSelector name="selectedQuests" />
								</div>

								{/* ASSIGN LESSON SECTION */}
								<div className="mt-5">
									<ClassroomSelector
										assignedClasses={assignedClasses}
										setAssignedClasses={setAssignedClasses}
									/>
								</div>

								{/* ASSIGNED CLASSES LIST */}
								{assignedClasses.length > 0 && (
									<div className="mt-4">
										<h5 className="fw-semibold">Assigned Class</h5>
										<div className="bg-light p-3 rounded">
											{assignedClasses.map((cls, index) => (
												<div
													key={index}
													className="d-flex flex-column flex-md-row justify-content-between align-items-md-center bg-red p-3 mb-2 rounded shadow-sm assigned-classes gap-2"
												>
													{/* CLASS NAME */}
													<span
														className="mb-2 mb-md-0 flex-grow-1"
														style={{ wordBreak: "break-word" }}
													>
														{cls.name}
													</span>

													{/* DATE + REMOVE BUTTON */}
													<div className="d-flex align-items-center gap-3 flex-shrink-0 mt-2 mt-md-0">
														{/* <div className="d-flex md:flex-row align-items-center flex-wrap"> */}
														<div className="d-flex flex-row align-items-center flex-wrap">
															<span className="small me-2 text-nowrap fw-semibold text-dark">
																Pick a Due Date :
															</span>
															<Form.Control
																type="date"
																value={cls.dueDate}
																min={new Date().toISOString().split("T")[0]}
																onChange={(e) =>
																	handleDueDateChange(index, e.target.value)
																}
																size="sm"
																style={{
																	width: "140px",
																	border: "2px solid #014782",
																	borderRadius: "6px",
																}}
															/>
														</div>

														<Button
															variant="light"
															className="p-0 border-0 "
															onClick={() => removeClass(index)}
															style={{
																background: "transparent",
															}}
														>
															<i className="bi bi-x-lg"></i>
														</Button>
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								{/* SUBMIT BUTTON */}
								<div className="mt-5">
									<Button
										type="submit"
										disabled={
											isSubmitting ||
											creatingLesson ||
											assigningQuestLoader ||
											assigningClassroomLoader
										}
										className="custom-primary-button"
									>
										{isSubmitting ||
											creatingLesson ||
											assigningQuestLoader ||
											assigningClassroomLoader
											? "Creating Lesson..."
											: "Create Lesson"}
									</Button>
								</div>
							</Form>
						);
					}}
				</Formik>
			</Card>
			<SuccessModal
				type={successMessage.type}
				showModal={showModal}
				handleClose={setShowModal}
				title={successMessage.message}
				message={successMessage.description}
				primaryButtonText="Add Another"
				secondaryButtonText="Lessons List"
				primaryButtonLink="/teacher/lesson/create"
				secondaryButtonLink="/teacher/lesson"
			/>
		</div>
	);
};

export default CreateLesson;
